import { FocusEvent, forwardRef, useCallback, useEffect, useMemo, useRef, useState, isValidElement } from "react";
import ReactSelect, { MultiValue, OptionsOrGroups, SelectInstance, SingleValue } from "react-select";

import { LoadingGroup } from "capitalrx-components-2.0/lib/general";

import { Body } from "../../typography";
import { isDescendentOf } from "../../utilities/isDescendentOf";
import { useInputDisabled, useInputReadonly } from "../InputGroup";
import { useSubmitting } from "../SubmitGroup";
import { useSimpleInputState } from "../useSimpleInputState";
import { normalizeAutocompleteValue } from "../utils/autocompleteHelpers";
import { DropdownProps } from "./Dropdown";
import { GroupItem } from "./GroupItem";
import { MultiSelectionProps } from "./MultiSelectionProps";
import { OptionItem } from "./OptionItem";
import { SharedDropdownPlaceholder } from "./SharedDropdownPlaceholder";
import * as Components from "./components";
import { flattenItems, getItems } from "./helpers";
import { AccessibilityProps } from "../../AccessibilityProps";

/**
 * The `SharedDropdown` component properties.
 */
export interface SharedDropdownProps
  extends Omit<DropdownProps, "instructions" | "tooltip" | "hideInstructions">,
    MultiSelectionProps, Pick<AccessibilityProps, "aria-label"> {
  /**
   * The input ID.
   */
  readonly id: string;
}

/**
 * The shared dropdown components.
 */
export const SharedDropdown = forwardRef<SelectInstance<OptionItem | null, boolean>, SharedDropdownProps>(
  function ShareDropdown(props, ref) {
    const {
      id,
      children,
      prefix,
      suffix,
      placeholder,
      tabIndex,
      autoFocus,
      defaultValue = null,
      clearable = false,
      searchable = false,
      searchInputProps,
      manualSearchFilter = false,
      noOptionsMessage,
      enableCheckboxOptions,
      selectedValues,
      label,
      hideLabel,
      "aria-label": ariaLabel,
      onChange,
      onFocusEnter,
      onFocusExit,
      onSearchChange: onSearchChangeProp,
      onMenuScrollToBottom,
      onSelectAll,
      isItemSelected = () => false,
      onRemove,
      autoComplete,
    } = props;
    const { value = defaultValue } = props;

    const getLabelText = (label: React.ReactNode): string | undefined => {
      if (typeof label === "string") return label;
      if (isValidElement(label) && typeof label.props.children === "string") {
        return label.props.children;
      }
      if (Array.isArray(label)) {
        return label.map(getLabelText).filter(Boolean).join(" ");
      }
      return undefined;
    };

    const items: OptionsOrGroups<OptionItem, GroupItem> = getItems(children);
    const flattenedItems = flattenItems(items);
    const [menuState, setMenuState] = useState({
      cancelled: false,
      open: false,
    });
    const parentRef = useRef<HTMLDivElement | null>(null);
    const searchRef = useRef<HTMLInputElement | null>(null);
    const [searchValue, onSearchChange] = useSimpleInputState<string | null>("");

    const changeHandler = useCallback(
      (newValue: SingleValue<OptionItem | null> | MultiValue<OptionItem | null>) => {
        if (Array.isArray(newValue)) return;
        const singleValue = newValue as SingleValue<OptionItem>;
        onChange?.(singleValue?.value ?? null, value);
      },
      [onChange, value],
    );

    const focusHandler = useCallback(() => {
      onFocusEnter?.();
    }, [searchable, onFocusEnter]);

    const blurHandler = useCallback(
      (ev: FocusEvent) => {
        const { relatedTarget } = ev;

        if (parentRef.current && relatedTarget) {
          if (isDescendentOf(parentRef.current, relatedTarget)) {
            setMenuState((current) => {
              return {
                ...current,
                cancelled: true,
              };
            });
            return;
          }
        }

        onFocusExit?.();
      },
      [onFocusExit],
    );

    const menuOpenHandler = useCallback(() => {
      setMenuState((current) => {
        if (current.cancelled) {
          return {
            ...current,
            cancelled: false,
          };
        }

        return {
          cancelled: false,
          open: true,
        };
      });
    }, []);

    const menuCloseHandler = useCallback(() => {
      setMenuState((current) => {
        if (current.cancelled) {
          return {
            ...current,
            cancelled: false,
          };
        }

        return {
          cancelled: false,
          open: false,
        };
      });
    }, []);

    useEffect(() => {
      if (menuState.open) {
        onSearchChange("", "");
        onSearchChangeProp?.("", "");
      }
    }, [!menuState.cancelled, menuState.open]);

    const searchBlurHandler = useCallback(
      (ev: FocusEvent) => {
        const { relatedTarget } = ev;

        ev.stopPropagation();

        if (relatedTarget === null || (parentRef.current && !isDescendentOf(parentRef.current, relatedTarget))) {
          setMenuState({
            cancelled: false,
            open: false,
          });

          onFocusExit?.();
        }
      },
      [onFocusExit],
    );

    const filter = useCallback(
      // eslint-disable-next-line
      (item: any) => {
        if (!searchable) return true;
        if (!searchValue) return true;

        const option = item.data as OptionItem | GroupItem;
        if (option.type === "group") return true;

        if (!option.searchText) {
          console.error("Dropdown with search enabled requires options to either have string content or the label property to be set.");
        } else if (option.searchText.toLowerCase().indexOf(searchValue.toLowerCase()) === -1) return false;

        return true;
      },
      [searchable, searchValue],
    );

    const selectedItem = useMemo(() => {
      if (value === null) return null;
      if (!flattenedItems || items.length === 0) return null;
      return flattenedItems.find((item) => item.value === value) ?? null;
    }, [flattenedItems, value]);

    const searchChangeHandler = useCallback(
      (value: string | null) => {
        onSearchChange(value, searchValue ?? null);
        onSearchChangeProp?.(value, searchValue ?? null);
      },
      [searchValue, onSearchChange, onSearchChangeProp],
    );

    const scrollToBottomHandler = useCallback(() => {
      onMenuScrollToBottom?.();
    }, [onMenuScrollToBottom]);

    const disabled = useInputDisabled(props);
    const readOnly = useInputReadonly(props);
    const submitting = useSubmitting();

    const autocompleteValue = useMemo(() => normalizeAutocompleteValue(autoComplete), [autoComplete]);

    let finalMenuOpen: boolean | undefined = undefined;
    if (searchable) finalMenuOpen = menuState.open;

    if (readOnly || submitting) finalMenuOpen = false;

    const closeMenuOnSelect = !enableCheckboxOptions;

    // Merge autocomplete into searchInputProps for react-select
    const finalSearchInputProps = useMemo(() => {
      const baseProps = searchInputProps || {};
      if (autocompleteValue) {
        return {
          ...baseProps,
          autoComplete: autocompleteValue,
        };
      }
      return baseProps;
    }, [searchInputProps, autocompleteValue]);

    const accessibleLabel = !label || hideLabel ? `dropdown ${id}` : undefined;

    return (
      <LoadingGroup.Placeholder fallback={<SharedDropdownPlaceholder className={props.className} style={props.style} />}>
        <div ref={parentRef}>
          <ReactSelect
            ref={ref}
            inputId={id}
            aria-label={ariaLabel ? ariaLabel ?? getLabelText(label) : accessibleLabel}
            aria-live="polite"
            aria-labelledby={!label || hideLabel ? undefined : `${id}-label`}
            autoFocus={autoFocus}
            tabIndex={readOnly || submitting || disabled ? -1 : (tabIndex ?? 0)}
            tabSelectsValue={enableCheckboxOptions && false}
            isSearchable={false}
            isMulti={false}
            isDisabled={disabled}
            isClearable={clearable}
            components={Components}
            placeholder={placeholder}
            options={items}
            value={selectedItem}
            closeMenuOnScroll
            captureMenuScroll
            escapeClearsValue={clearable}
            onChange={changeHandler}
            menuIsOpen={finalMenuOpen}
            openMenuOnClick={!readOnly && !submitting}
            onFocus={focusHandler}
            onBlur={blurHandler}
            onMenuOpen={menuOpenHandler}
            menuShouldScrollIntoView={false}
            closeMenuOnSelect={closeMenuOnSelect}
            onMenuClose={menuCloseHandler}
            onMenuScrollToBottom={scrollToBottomHandler}
            filterOption={!manualSearchFilter ? filter : undefined}
            noOptionsMessage={() => noOptionsMessage || <Body textAlign="center">No options</Body>}
            className={typeof props.className === "string" ? props.className : undefined}
            // custom properties
            {...{
              prefix,
              suffix,
              searchable,
              searchInputProps: finalSearchInputProps,
              searchRef,
              searchValue,
              onSearchChange: searchChangeHandler,
              onSearchBlur: searchBlurHandler,
              isReadOnly: readOnly || submitting,
              enableCheckboxOptions,
              selectedValues,
              showMasterCheck: false,
              onSelectAll,
              isItemSelected,
              onRemove,
            }}
          />
        </div>
      </LoadingGroup.Placeholder>
    );
  },
);
